<template>
  <div class="pos-container">
    <!-- Header con controles principales -->
    <div class="pos-header">
      <div class="d-flex justify-content-between align-items-center">

        <div class="header-controls">
          <button @click="returnHome" type="button" class="btn btn-danger ms-2">
            <i class="fas fa-hand-point-left me-1"></i>
          </button>
          <button
            id="buttonMasVendidos"
            class="btn-toggle"
            :class="{ active: showBestSellers }"
            @click="toggleBestSellers"
          >
            <i class="fas fa-star me-1"></i>
            {{ showBestSellers ? 'Ocultar Más Vendidos' : 'Mostrar Más Vendidos' }}
          </button>
          <div class="btn-group-toggle ms-2">
            <button
              class="btn-toggle"
              :class="{ active: viewMode === 'grid' }"
              @click="viewMode = 'grid'"
            >
              <i class="fas fa-th me-1"></i>Cuadrícula
            </button>
            <button
              class="btn-toggle"
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <i class="fas fa-list me-1"></i>Lista
            </button>
          </div>
        </div>
        
        <div class="cart-summary">
          <span class="total-pill">
            <strong>Total: S/{{ cartTotal.toFixed(2) }}</strong>
          </span>
          <button 
            class="btn-quote ms-2"
            @click="mtdOpenQuotes"
          >
            <i class="fas fa-file-alt me-1"></i>Cotización
          </button>
          <button 
            class="btn-finish ms-2"
            @click="mtdOpenModalSale"
          >
            <i class="fas fa-check me-1"></i>Finalizar Venta
          </button>
        </div>
      </div>
    </div>

    <div class="pos-content">
      <!-- Panel de productos más vendidos -->
      <div v-if="showBestSellers" class="best-sellers-panel">
        <div class="panel-header">
          <h6 class="mb-0">
            <i class="fas fa-star text-warning me-2"></i>
            Productos Más Vendidos
          </h6>
        </div>
        <div class="best-sellers-list">
          <div 
              v-for="product in popularProducts" 
              :key="product.value"
              class="best-seller-item"
              @click="addToCart(product)"
          >
            <img :src="product.image" :alt="product.label" class="product-thumb">
            <div class="product-info">
            <div class="product-name">{{ product.label }}</div>
            <div class="product-price">S/{{ product.price }}</div>
            </div>
            <div class="stock-badge" :class="getStockClass(product.stock)">
            {{ product.stock }}
            </div>
          </div>
        </div>
      </div>

      <!-- Área principal de productos -->
      <div class="main-content" :class="{ 'full-width': !showBestSellers }">
        <!-- Buscador -->
        <div class="search-section">
          <div class="search-input-group">
            <input 
              type="text" 
              class="form-control search-input"
              placeholder="Buscar por código o nombre de producto..."
              v-model="searchQuery"
              @input="handleSearchInput"
              @keyup.enter="quickAdd"
            >
            <button class="btn search-btn" @click="searchProducts">
              <i class="fas fa-search"></i>
            </button>
          </div>
        </div>

        <!-- Vista de productos -->
        <div class="products-section">
          <!-- Lista/Cuadrícula de productos -->
          <div class="products-display p-3">
            <!-- Vista de Cuadrícula -->
            <div v-if="viewMode === 'grid'" class="row g-3">
              <div 
                v-for="(product, index) in filterProducts" :key="index"
                class="col-xl-3 col-lg-4 col-md-6 col-sm-12 p-2"
              >
                <div 
                  class="card product-card h-100 cursor-pointer hover-shadow" 
                  @click="addToCart(product)"
                >
                  <img 
                    :src="product.image || '/placeholder.svg?height=150&width=150'" 
                    class="card-img-top" 
                    style="height: 120px; object-fit: cover;"
                  >
                  <div class="card-body p-2">
                    <h6 class="card-title small mb-1"><strong>{{ product.label }}</strong></h6>
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <small class="text-muted">Compra: S/{{ product.purchase_price }}</small>
                      <small class="text-success fw-bold"><strong>Venta: S/{{ product.price }}</strong></small>
                    </div>
                    <!-- Removed "Click para añadir" text and made stock badge full width -->
                    <div class="text-center">
                      <span 
                        class="badge w-100" 
                        :class="product.stock <= 5 ? 'bg-danger' : product.stock <= 10 ? 'bg-warning' : 'bg-success'"
                      >
                        Stock: {{ product.stock }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Vista en lista -->
            <div v-if="viewMode === 'list'" class="products-list">
              <div class="list-header">
                <div class="row">
                  <div class="col-sm-6 col-md-2">Imagen</div>
                  <div class="col-sm-6 col-md-5">Producto</div>
                  <div class="col-sm-6 col-md-2">P. Compra</div>
                  <div class="col-sm-6 col-md-2">P. Venta</div>
                  <div class="col-sm-6 col-md-1">Stock</div>
                </div>
              </div>
              <div 
                v-for="(product, index) in filterProducts" :key="index"
                class="list-item"
                @click="addToCart(product)"
              >
                <div class="row align-items-center">
                  <div class="col-sm-6 col-md-2">
                    <img :src="product.image" :alt="product.label" class="list-image">
                  </div>
                  <div class="col-sm-6 col-md-5">
                    <strong>{{ product.label }}</strong>
                  </div>
                  <div class="col-sm-6 col-md-2">S/{{ product.purchase_price }}</div>
                  <div class="col-sm-6 col-md-2">S/{{ product.price }}</div>
                  <div class="col-sm-6 col-md-1">
                    <span class="badge" :class="getStockBadgeClass(product.stock)">
                      {{ product.stock }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel de carrito lateral (debe estar aquí, no fuera de pos-content) -->
      <div class="cart-panel-side">
        <div class="cart-header">
          <h6 class="mb-0">
              <i class="fas fa-shopping-cart me-2"></i>
              Carrito de Compras ({{ productList.length }} items)
          </h6>
        </div>
        <div class="cart-items">
          <div 
              v-for="(item, index) in productList" :key="index"
              class="cart-item"
          >
            <div class="item-info">
              <div 
                class="item-name clickeable-price"
                @click="viewPriceAndStock(item)"
                :title="'Click para ver stock'"
              >
              {{ item.product.name }}
              </div>
              <div 
                class="item-price clickeable-price" 
                @click="editPrice(item)"
                :title="'Click para editar precio'"
              >
                S/{{ item.price }} c/u
                <small v-if="item.selectedPriceType === 'wholesale'" class="price-type-indicator">(Por Mayor)</small>
              </div>
            </div>
            <div class="item-controls">
              <button class="btn btn-sm btn-outline-secondary" @click="decreaseQuantity(item)">
                  <i class="fas fa-minus"></i>
              </button>
              <span 
                  class="quantity-display"
                  @click="editQuantity(item)"
              >
                  {{ item.quantity }}
              </span>
              <button class="btn btn-sm btn-outline-secondary" @click="increaseQuantity(item)">
                  <i class="fas fa-plus"></i>
              </button>
            </div>
            <div class="item-total">
              S/{{ (item.price * item.quantity).toFixed(2) }}
              <button class="btn btn-sm btn-outline-danger" @click="removeFromCart(item)">
              <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Carrito flotante móvil -->
    <div class="cart-panel-mobile d-lg-none">
      <div class="cart-mobile-summary" @click="toggleMobileCart">
        <div>
          <i class="fas fa-shopping-cart me-2"></i>
          <strong>{{ productList.length }} items</strong>
        </div>
        <div>
          <strong>Total: S/{{ cartTotal.toFixed(2) }}</strong>
          <i class="ms-2 fas" :class="showMobileCart ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </div>
      </div>
      <div v-if="showMobileCart" class="cart-mobile-items">
        <div 
          v-for="(item, index) in productList" :key="index"
          class="cart-item"
        >
          <div class="item-info">
            <!-- <div class="item-name">{{ item.product.name }}</div> -->
            <!-- <div class="item-price">S/{{ item.price }} c/u</div> -->
            <div 
              class="item-name clickeable-price"
              @click="viewPriceAndStock(item)"
              :title="'Click para ver stock'"
            >
            {{ item.product.name }}
            </div>
            <div 
              class="item-price clickeable-price" 
              @click="editPrice(item)"
              :title="'Click para editar precio'"
            >
              S/{{ item.price }} c/u
              <small v-if="item.selectedPriceType === 'wholesale'" class="price-type-indicator">(Por Mayor)</small>
            </div>
          </div>
          <div class="item-controls">
            <button class="btn btn-sm btn-outline-secondary" @click="decreaseQuantity(item)">
              <i class="fas fa-minus"></i>
            </button>
            <span class="quantity-display" @click="editQuantity(item)">
              {{ item.quantity }}
            </span>
            <button class="btn btn-sm btn-outline-secondary" @click="increaseQuantity(item)">
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <div class="item-total">
            S/{{ (item.price * item.quantity).toFixed(2) }}
            <button class="btn btn-sm btn-outline-danger ms-1" @click="removeFromCart(item)">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para editar cantidad -->
    <div v-if="showQuantityModal" class="modal-overlay" @click="closeQuantityModal">
      <div class="quantity-modal" @click.stop>
        <h6>Editar Cantidad</h6>
        <div class="mb-3">
          <strong>{{ selectedItem?.name }}</strong>
        </div>
        <div class="input-group">
          <input 
            type="number" 
            class="form-control" 
            v-model="editingQuantity"
            min="1"
            @keyup.enter="saveQuantity"
          >
          <button class="btn btn-primary" @click="saveQuantity">
            Guardar
          </button>
        </div>
        <button class="btn btn-secondary btn-sm mt-2" @click="closeQuantityModal">
          Cancelar
        </button>
      </div>
    </div>

    <!-- Modal para editar precios -->
    <div v-if="showPriceModal" class="modal-overlay" @click="closePriceModal">
      <div class="price-modal" @click.stop>
        <div class="modal-header">
          <h6>EDITAR PRECIO DE VENTA</h6>
        </div>
        
        <div class="modal-body">
          <div class="mb-3">
            <strong>{{ selectedPriceItem?.product.name }}</strong>
          </div>
          
          <!-- Precio Venta Normal -->
          <div class="price-option mb-3">
            <div class="d-flex align-items-center mb-2">
              <div class="radio-container">
                <input 
                  type="radio" 
                  id="priceNormal" 
                  value="normal" 
                  v-model="selectedPriceType"
                  class="form-check-input"
                >
                <label for="priceNormal" class="form-check-label ms-2">
                  <i class="fas fa-circle text-success me-1"></i>
                  Precio Venta
                </label>
              </div>
            </div>
            <input 
              type="number" 
              class="form-control"
              v-model="editingPrice"
              :disabled="selectedPriceType !== 'normal'"
              step="0.01"
              min="0"
            >
          </div>
          
          <!-- Precio Venta por Mayor -->
          <div class="price-option mb-3">
            <div class="d-flex align-items-center mb-2">
              <div class="radio-container">
                <input 
                  type="radio" 
                  id="priceWholesale" 
                  value="wholesale" 
                  v-model="selectedPriceType"
                  class="form-check-input"
                >
                <label for="priceWholesale" class="form-check-label ms-2">
                  <i class="fas fa-circle text-primary me-1"></i>
                  Precio Venta x Mayor
                </label>
              </div>
            </div>
            <input 
              type="number" 
              class="form-control"
              v-model="editingWholesalePrice"
              :disabled="selectedPriceType !== 'wholesale'"
              step="0.01"
              min="0"
            >
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closePriceModal">
            CANCELAR
          </button>
          <button class="btn btn-primary" @click="savePriceChanges">
            GUARDAR
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para ver stock y precios -->
    <div v-if="showInfoStockModal" class="modal-overlay" @click="closeInfoStockModal">
      <div class="price-modal" style="max-width: 700px; width: 90%;" @click.stop>
        <div class="modal-header">
          <h6>INFORMACIÓN DE STOCK</h6>
        </div>
        
        <div class="modal-body" v-if="selectedPriceItem">
          <!-- Parte Superior: Info del Producto -->
          <div class="row mb-4">
            <div class="col-md-4 text-center">
              <img 
                :src="selectedPriceItem.productObj?.image || selectedPriceItem.image || '/product_default_2.png'" 
                class="img-fluid rounded border" 
                style="max-height: 120px; object-fit: cover;"
              >
            </div>
            <div class="col-md-8">
              <h5 class="text-primary mb-2"><strong>{{ selectedPriceItem.productObj?.label || selectedPriceItem.product?.name }}</strong></h5>
              <div class="mb-1">
                <strong>Sede Actual:</strong> <span class="badge badge-success text-white">{{ $store.getters.get_campu?.name || 'No definida' }}</span>
              </div>
              <div class="row mt-2">
                <div class="col-6 mb-2">
                  <small class="text-muted d-block">P. Compra</small>
                  <strong>S/{{ selectedPriceItem.productObj?.purchase_price ?? selectedPriceItem.purchase_price ?? '0.00' }}</strong>
                </div>
                <div class="col-6 mb-2">
                  <small class="text-muted d-block">P. Venta</small>
                  <strong class="text-success">S/{{ selectedPriceItem.productObj?.price ?? selectedPriceItem.price ?? '0.00' }}</strong>
                </div>
                <div class="col-6">
                  <small class="text-muted d-block">P. Mayor</small>
                  <strong class="text-primary">S/{{ selectedPriceItem.productObj?.whole_sale_price ?? selectedPriceItem.wholesalePrice ?? '0.00' }}</strong>
                </div>
                <div class="col-6">
                  <small class="text-muted d-block">Stock Local</small>
                  <span class="badge" :class="getStockBadgeClass(selectedPriceItem.productObj?.stock ?? selectedPriceItem.stock)">
                    {{ selectedPriceItem.productObj?.stock ?? selectedPriceItem.stock }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Parte Inferior: Tabla de Stocks -->
          <div class="border-top pt-3">
            <h6 class="mb-3 text-secondary"><strong>STOCKS POR ALMACÉN</strong></h6>
            <div class="table-responsive" style="max-height: 250px; overflow-y: auto;">
              <table class="table table-sm table-striped table-bordered text-center align-middle">
                <thead class="table-dark">
                  <tr>
                    <th>SEDE - ALMACÉN</th>
                    <th>STOCK</th>
                    <th>P. COMPRA</th>
                    <th>P. VENTA</th>
                    <th>P. MAYOR</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(st, idx) in (selectedPriceItem.productObj?.stocks || selectedPriceItem.stocks || [])" :key="idx">
                    <td class="text-start">
                      <!-- <strong>{{ st.sede_name || '-' }}</strong> - {{ st.almacen_name || '-' }} -->
                      <strong>{{ st.sede_name || '-' }}</strong> <!-- - {{ st.almacen_name || '-' }} -->
                    </td>
                    <td>
                      <span class="badge" :class="getStockBadgeClass(st.stock)">
                        {{ st.stock }}
                      </span>
                    </td>
                    <td>S/{{ st.purchase_price || st.product_unit?.product?.price?.[0]?.purchase_price || selectedPriceItem.productObj?.purchase_price || '0.00' }}</td>
                    <td class="text-success fw-bold">S/{{ st.sale_price || st.product_unit?.product?.price?.[0]?.sale_price || selectedPriceItem.productObj?.price || '0.00' }}</td>
                    <td class="text-primary fw-bold">S/{{ st.whole_sale_price || st.product_unit?.product?.price?.[0]?.whole_sale_price || selectedPriceItem.productObj?.whole_sale_price || '0.00' }}</td>
                  </tr>
                  <tr v-if="!(selectedPriceItem.productObj?.stocks || selectedPriceItem.stocks)?.length">
                    <td colspan="5" class="text-muted text-center py-3">No hay registros de stock en otros almacenes.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeInfoStockModal">
            CERRAR
          </button>
        </div>
      </div>
    </div>

    <div class="container mx-auto">
      <!-- Existing content -->
      <cModalSale
        :title="modalSale.title"
        :productList="productList"
        :sale="sale"
        :boo_modal="modalSale.modal_form"
        @closeModalSale="closeModalSale"
        @save="handleSave"
      />

      <cModalUpdateSale
        :title="modalUpdateSale.title"
        :boo_modal="modalUpdateSale.modal_form"
        :product="modalUpdateSale.product"
        @closeModalUpdateSale="closeModalUpdateSale"
        @updateProduct="handleUpdateProduct"
        :data_product="data_product"
      />

      <cModalQuotes
        ref="cQuotes"
        :title="modalQuotes.title"
        :boo_modal="modalQuotes.modal_form"
        :sale="sale"
        @mtdClose="closeModalQuotes"
        @save="saveQuotes"
      />
    </div>

  </div>
</template>

<script>
import { mapActions } from "vuex";
import cModalSale from "../modals/cModalSale.vue";
import cModalUpdateSale from "../modals/cModalUpdateSale.vue";
import cModalQuotes from "../modals/cModalQuotes.vue";

export default {
  name: 'sales',
  components: {
    cModalSale,
    cModalUpdateSale,
    cModalQuotes,
  },
  data() {
    return {
      showBestSellers: true,
      viewMode: 'grid',
      prefix: "sales",
      data_product: [],
      estado: true,
      searchQuery: "", // Para almacenar lo que escribe el usuario
      searchBarcode: "", // Para almacenar lo que escribe el usuario
      filteredProducts: [], // Para almacenar los productos filtrados
      filteredProductsBarcode: [], // Para almacenar los productos filtrados
      productList: [],
      popularProducts: [],
      sale: {
        id: "",
        company_id: this.$store.getters.get_company.id,
        campu_id: this.$store.getters.get_campu.id,
        user_id: this.$store.getters.get__user_id,
        document_type_id: "",
        date: "",
        method: "",
        type: "",
        total: 0 /** monto total del servicio */,
        amount: 0 /**monto pagado - adelanto */,
        pending: 0 /** pendiente */,
        ruc: "",
        state: 0,
        entity_document: "",
        entity_name: "",
        entity_address: "",
        entity_obs: "",
        typeAjustment: 0 /** tipo de ajuste % o S/ */,
        adjustment: 0 /** monto de ajuste */,
        new_pending: "" /** pendiente luego del ajuste */,
        pendigPayment: 0,
        option: 0,
      },
      newProduct: {
        product: {
          id: "",
          name: "",
        },
        price: "",
        quantity: 1,
        subtotal: 0,
        stock: 0,
      },
      modalSale: {
        title: "Terminar Venta",
        modal_form: false,
      },
      modalUpdateSale: {
        title: "Editar",
        modal_form: false,
        product: {
          product: {
            id: "",
            name: "",
          },
          price: "",
          quantity: "",
          subtotal: "",
          code: "",
        },
      },
      modalProductList: {
        modal_form: false,
      },
      modalQuotes: {
        title: "COTIZACIÓN",
        modal_form: false,
      },
      showQuantityModal: false,
      selectedItem: null,
      editingQuantity: 1,
      // modal de precios
      showPriceModal: false,
      showInfoStockModal: false,
      selectedPriceItem: null,
      editingPrice: 0,
      editingWholesalePrice: 0,
      selectedPriceType: 'normal', // 'normal' o 'wholesale'
      showMobileCart: false,
    }
  },
  
  computed: {
    filterProducts() {
      // Agrega una validación defensiva
      if (!Array.isArray(this.data_product)) {
        return [];
      }
      
      if (!this.searchQuery) return this.data_product;
      const query = this.searchQuery.toUpperCase();
      return this.data_product.filter(product => {
        // Validación para label
        const labelMatch = product.label && product.label.toUpperCase().includes(query);
        // Validación para barcodes
        let barcodeMatch = false;
        if (typeof product.barcodes === 'string') {
          barcodeMatch = product.barcodes.toUpperCase().includes(query);
        } else if (Array.isArray(product.barcodes)) {
          barcodeMatch = product.barcodes.some(barcode => 
            barcode && typeof barcode === 'string' && barcode.toUpperCase().includes(query)
          );
        }
        return labelMatch || barcodeMatch;
      });
    },
    cartTotal() {
      return this.productList.reduce((total, item) => 
        total + (item.price * item.quantity), 0
      );
    },
  },

  watch: {
    productList: {
      handler() {
        this.sale.total = this.cartTotal.toFixed(2);
      },
      deep: true,
    },
  },

  created() {
    this.showProduct();
    this.showPopularProducts();
  },
  
  methods: {
    ...mapActions(["get", "post"]),
    returnHome() {
      this.$emit("returnHome");
    },
    toggleMobileCart() {
      this.showMobileCart = !this.showMobileCart;
    },
    handleSearchInput(event) {
      this.searchQuery = event.target.value.toUpperCase();
    },
    showPopularProducts() {
      this.get({
        url:
          this.$store.getters.get__url +
          "/" +
          this.prefix +
          "/byPopularProducts/" +
          this.$store.getters.get_campu.id,
        token: this.$store.getters.get__token,
      })
        .then((response) => {
          const stockMap = new Map();
          // Mapeo de stock por product_unit_id para acceso rápido
          response.data.stock.forEach(({ product_unit, stock, stocks }) => {
            stockMap.set(product_unit.id, {
              productName: product_unit.product.name,
              productId: product_unit.product.id,
              unitName: product_unit.unit.name,
              unitId: product_unit.unit.id,
              price: product_unit.product.price[0].sale_price,
              stock: stock,
              stocks: stocks,
            });
          });
          // Construcción de popularProducts combinando la información del stock y popular
          response.data.popular.forEach(
            ({ product_unit_id, total_quantity }, index) => {
              if (index < 7) {
                const stockInfo = stockMap.get(product_unit_id);
                if (stockInfo) {
                  this.popularProducts.push({
                    label: stockInfo.productName,
                    value: stockInfo.productId,
                    label_unit: stockInfo.unitName,
                    value_unit: stockInfo.unitId,
                    price: stockInfo.price,
                    quantity: total_quantity,
                    stock: stockInfo.stock,
                    stocks: stockInfo.stocks,
                    image: '/product_default.png?height=200&width=200'
                  });
                }
              }
            }
          );
          this.popularProducts = this.popularProducts.slice(0, 7);
        })
        .catch((errors) => {
          console.log(errors);
          
        });
    },
    showProduct() {
      this.get({
        url:
          this.$store.getters.get__url +
          "/" +
          this.prefix +
          "/byProduct/" +
          this.$store.getters.get_campu.id,
        token: this.$store.getters.get__token,
      })
        .then((response) => {
          response.data.data.forEach((arr) => {
            const { stock, product_unit, stocks } = arr;
            this.data_product.push({
              label: product_unit.product.name,
              value: product_unit.product.id,
              price: product_unit.product.price[0].sale_price,
              purchase_price: product_unit.product.price[0].purchase_price,
              whole_sale_price: product_unit.product.price[0].whole_sale_price,
              value_unit: product_unit.unit.id,
              label_unit: product_unit.unit.name,
              barcodes: product_unit.product.barcode,
              stock: stock,
              stocks: stocks,
              type: 1, // producto
              image: '/product_default_2.png?height=200&width=200'
            });
          });
          /** kit */
          response.data.kit.forEach((arr) => {
            this.data_product.push({
              label: arr.name,
              value: arr.id,
              price: arr.price,
              value_unit: 1,
              label_unit: "Unidad",
              barcodes: "",
              stock: 0,
              detail: arr.detail,
              type: 2, // kit
              image: '/product_default_2.png?height=200&width=200'
            });
          });
        })
        .catch((errors) => {
          console.log(errors);
        });
    },
    addPopularProduct(item) {
      const existingProduct = this.productList.find(
        (product) => product.product.name === item.label
      );
      if (existingProduct) {
        existingProduct.quantity += 1;
      } else {
        const productToAdd = {
          product: {
            id: item.value, // Use item.value for the ID
            name: item.label,
          },
          unit: {
            id: item.value_unit,
            name: item.label_unit,
          },
          price: item.price,
          quantity: 1,
          subtotal: item.price,
          stock: item.stock,
          kit: null,
        };
        this.productList.push(productToAdd);
      }
    },
    // Existing methods
    handleSave() {
      // Restablecer la lista de productos y la venta después de guardar
      this.productList = [];
      this.sale = {
        id: "",
        company_id: this.$store.getters.get_company.id,
        campu_id: this.$store.getters.get_campu.id,
        user_id: this.$store.getters.get__user_id,
        document_type_id: "",
        date: "",
        method: "",
        type: "",
        total: 0 /** monto total del servicio */,
        amount: 0 /**monto pagado - adelanto */,
        pending: 0 /** pendiente */,
        ruc: "",
        state: 0,
        entity_document: "",
        entity_name: "",
        entity_address: "",
        typeAjustment: 0 /** tipo de ajuste % o S/ */,
        adjustment: 0 /** monto de ajuste */,
        new_pending: "" /** pendiente luego del ajuste */,
        pendigPayment: 0,
        option: 0,
        entity_obs: "",
      };
      this.modalSale.modal_form = false;
      //limpio datos
      this.data_product = [];
      this.popularProducts = [];
      //recargo denuevo
      this.showProduct();
      this.showPopularProducts();
    },
    mtdOpenModalSale() {
      if(this.productList.length == 0) {
        Swal.fire({
          title: "Agregue un Producto al Carrito",
          icon: "warning",
        });
        return;
      }else{
        this.modalSale = {
          title: "TERMINAR VENTA",
          modal_form: true,
        };
      }
      
    },
    closeModalSale() {
      this.modalSale = {
        modal_form: false,
      };
      this.sale.entity_document = "";
      this.sale.entity_name = "";
    },
    openProductModal(product) {
      this.modalUpdateSale = {
        title: "Editar",
        modal_form: true,
        product: product,
      };
    },
    closeModalUpdateSale() {
      this.modalUpdateSale = {
        modal_form: false,
      };
    },
    handleUpdateProduct(updatedProduct) {
      const index = this.productList.findIndex(
        (p) => p.product.id === updatedProduct.product.id
      );
      if (index !== -1) {
        this.$set(this.productList, index, updatedProduct); // Actualiza el producto en la lista
      }
    },
    //Cotizacion
    mtdOpenQuotes() {
      if (this.productList.length == 0) {
        Swal.fire({
          title: "Agregue un Producto al Carrito",
          icon: "warning",
        });
      } else {
        this.modalQuotes = {
          title: "COTIZACIÓN",
          modal_form: true,
        };
      }
    },
    closeModalQuotes() {
      this.modalQuotes = {
        title: "COTIZACIÓN",
        modal_form: false,
      };
    },
    saveQuotes(data, total, pagoTotal,client) {
      this.post({
        url: this.$store.getters.get__url + "/quotes/store",
        token: this.$store.getters.get__token,
        params: {
          data: data,
          total: total,
          pagoTotal: pagoTotal,
          products: this.productList,
          company_id: this.$store.getters.get_company.id,
          campu_id: this.$store.getters.get_campu.id,
          user_id: this.$store.getters.get__user_id,
        },
      })
        .then((response) => {
          console.log(response);
          
          if (response.statusCode == 200) {
            Swal.fire({
              title: "Registrado Correctamente",
              icon: "success",
            });
            this.mtdProccessSaleResponse(response,client.name ,client.document, "");
            // this.viewpdfQuotes(response.data.id);
            this.productList = [];
            this.$nextTick(() => {
              if (this.$refs.cQuotes && this.$refs.cQuotes.mtdClear) {
                this.$refs.cQuotes.mtdClear();
              }
            });
            this.closeModalQuotes();
          }
        })
        .catch((errors) => {
          Swal.fire({
            title: errors,
            icon: "error",
          });
        });
    },
    viewpdfQuotes: function (id) {
      window.open(
        this.$store.getters.get__url + "/viewpdfQuotes/" + id,
        "_blank"
      );
    },
    mtdProccessSaleResponse: async function (response, name, document, phone) {
      const result = await Swal.fire({
        title: "Cotización Finalizada",
        text: "Seleccione una opción",
        icon: "success",
        showCancelButton: true,
        showDenyButton: false,
        confirmButtonText: '<i class="fas fa-file-pdf"></i> Imprimir PDF',
        cancelButtonText: '<i class="fab fa-whatsapp"></i> Enviar a WhatsApp',
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-success",
          actions: "d-flex justify-content-between gap-2",
        },
        buttonsStyling: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: true,
        showCloseButton: true,
      });
      if (result.isConfirmed) {
        this.viewpdfQuotes(response.data.id);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        // let phone = celular;
        if (!phone) {
          await Swal.fire({
            title: "No hay número registrado",
            text: "Por favor, ingresa un número de teléfono para enviar el comprobante.",
            icon: "warning",
            input: "text",
            inputPlaceholder: "Ingrese su número de teléfono",
            showCancelButton: true,
          }).then(async (inputResult) => {
            if (inputResult.isConfirmed && inputResult.value) {
              phone = inputResult.value;
            } else {
              return;
            }
          });
        } else {
          await Swal.fire({
            title: "Confirmar número de teléfono",
            text: "Por favor, revisa o edita el número de teléfono antes de enviar el comprobante.",
            icon: "info",
            input: "text",
            inputValue: phone,
            showCancelButton: true,
          }).then(async (inputResult) => {
            if (inputResult.isConfirmed && inputResult.value) {
              phone = inputResult.value;
            } else {
              return;
            }
          });
        }
        let urlPdf = this.$store.getters.get__url + "/viewpdfQuotes/" + response.data.id;
        await this.post({
          url: this.$store.getters.get__url + "/send/wsp/sale",
          token: this.$store.getters.get__token,
          params: {
            phone: phone,
            fileUrl: urlPdf,
            filename: `cotizacion-${response.data.id}.pdf`,
            message: `Hola ${name}, te enviamos el comprobante de cotizacion en *AQ Industrias*. \nGracias por tu preferencia. \n¡Quedamos atentos para cualquier consulta!.\nSaludos`,
            document: document
          },
        });

        await Swal.fire({
          title: "Mensaje enviado",
          text:
            "El comprobante electrónico se ha enviado correctamente a " + phone,
          icon: "success",
        });
      }
    },
    toggleBestSellers() {
      this.showBestSellers = !this.showBestSellers;
    },
    addToCart(product) {
      // Si el producto está en stock 0 o menor, mostrar alerta y no agregar
      if (product.stock <= 0) {
        const stocks = product.stocks || [];
        let stocksHtml = "";
        
        if (stocks.length > 0) {
          stocksHtml = `
            <div style="margin-top: 12px; text-align: left;">
              <strong style="display: block; margin-bottom: 6px; font-size: 0.9em; color: #4b5563; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px;">INFORMACIÓN DE STOCK:</strong>
              <table style="width: 100%; border-collapse: collapse; font-size: 0.8em; line-height: 1.2;">
                <thead>
                  <tr style="background-color: #f3f4f6; border-bottom: 2px solid #e5e7eb; color: #374151;">
                    <th style="padding: 4px; text-align: left; width: 100px;">SEDE</th>
                    <th style="padding: 4px; text-align: center; width: 70px;">STOCK</th>
                    <th style="padding: 4px; text-align: right; width: 70px;">P. COMPRA</th>
                    <th style="padding: 4px; text-align: right; width: 70px;">P. VENTA</th>
                  </tr>
                </thead>
                <tbody>
                  ${stocks.map(st => {
                    const purchasePrice = st.purchase_price || product.purchase_price || '0.00';
                    const salePrice = st.sale_price || product.price || '0.00';
                    return `
                      <tr style="border-bottom: 1px solid #f3f4f6;">
                        <td style="padding: 4px; font-weight: bold; color: #4b5563;">${st.sede_name || '-'}</td>
                        <td style="padding: 4px; text-align: center;">
                          <span style="display: inline-block; padding: 1px 5px; border-radius: 3px; font-size: 0.85em; font-weight: bold; 
                            background-color: ${st.stock <= 5 ? '#fde8e8' : (st.stock <= 10 ? '#fef3c7' : '#def7ec')}; 
                            color: ${st.stock <= 5 ? '#9b1c1c' : (st.stock <= 10 ? '#92400e' : '#03543f')};">
                            ${st.stock}
                          </span>
                        </td>
                        <td style="padding: 4px; text-align: right; color: #6b7280;">S/${purchasePrice}</td>
                        <td style="padding: 4px; text-align: right; font-weight: bold; color: #047857;">S/${salePrice}</td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
              </table>
            </div>
          `;
        } else {
          stocksHtml = `<p style="margin-top: 8px; color: #9ca3af; font-size: 0.8em; font-style: italic; text-align: center;">No hay registros de stock en otras sedes.</p>`;
        }

        Swal.fire({
          title: "Producto sin stock",
          html: `
            <p style="margin-bottom: 8px; font-size: 0.95em;">El producto <strong>"${product.label}"</strong> no tiene stock disponible en esta sede.</p>
            ${stocksHtml}
          `,
          icon: "warning",
          customClass: {
            popup: 'swal-wide-custom'
          }
        });
        return;
      }

      const existingItem = this.productList.find(item => item.product.id === product.value);
      if (existingItem) {
        // Validar que no exceda el stock disponible
        if (existingItem.quantity >= product.stock) {
          Swal.fire({
            title: "Stock insuficiente",
            text: `No puedes agregar más unidades. Stock disponible: ${product.stock}`,
            icon: "warning",
          });
          return;
        }
        existingItem.quantity += 1;
      } else {
        const newProduct = {
          product: {
            id: product.value,
            name: product.label,
          },
          unit: {
            id: product.value_unit,
            name: product.label_unit,
          },
          price: product.price,
          originalPrice: product.price, // Guardar precio original
          wholesalePrice: product.wholesalePrice || 0, // Precio por mayor
          selectedPriceType: 'normal', // Por defecto precio normal
          code: product.barcodes ? product.barcodes[0].name : product.code,
          quantity: 1,
          subtotal: product.price,
          stock: product.stock,
          productObj: product
        };
        this.productList.push(newProduct);
      }
    },
    increaseQuantity(item) {
      // Validar que no exceda el stock disponible
      if (item.quantity >= item.stock) {
        Swal.fire({
          title: "Stock insuficiente",
          text: `No puedes agregar más unidades. Stock disponible: ${item.stock}`,
          icon: "warning",
        });
        return;
      }
      item.quantity += 1;
    },
    decreaseQuantity(item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
      }
    },
    removeFromCart(item) {
      /* const index = this.productList.findIndex(cartItem => cartItem.id === item.id);
      if (index > -1) {
        this.productList.splice(index, 1);
      } */
      const index = this.productList.findIndex(cartItem => 
        cartItem.product.id === item.product.id && 
        cartItem.unit.id === item.unit.id && 
        cartItem.is_production === item.is_production
      );
      if (index > -1) {
        this.productList.splice(index, 1);
      }
    },
    editQuantity(item) {
      this.selectedItem = item;
      this.editingQuantity = item.quantity;
      this.showQuantityModal = true;
    },
    saveQuantity() {
      if (this.selectedItem && this.editingQuantity > 0) {
        console.log(this.selectedItem);
        
        if (this.editingQuantity > parseInt(this.selectedItem.stock)) {
          Swal.fire({
            title: "Stock insuficiente",
            text: `No puedes establecer una cantidad mayor al stock disponible: ${this.selectedItem.stock}`,
            icon: "warning",
          });
          return;
        }
        this.selectedItem.quantity = parseInt(this.editingQuantity);
      }
      this.closeQuantityModal();
    },
    closeQuantityModal() {
      this.showQuantityModal = false;
      this.selectedItem = null;
      this.editingQuantity = 1;
    },
    quickAdd() {
      if (this.filteredProducts.length === 1) {
        this.addToCart(this.filteredProducts[0]);
        this.searchQuery = '';
      }
    },
    editPrice(item) {
      this.selectedPriceItem = item;
      this.selectedPriceType = item.selectedPriceType || 'normal';
      // Cargar los precios originales del producto
      const originalProduct = item.productObj || item;
      // Si tiene precio por mayor guardado, úsalo, sino usa el precio original del producto
      this.editingWholesalePrice = item.wholesalePrice || originalProduct.wholesalePrice || 0;
      // Cargar el precio ingresado antes de
      if (item.selectedPriceType === 'wholesale') {
        this.editingPrice = originalProduct.price || item.price;
      } else {
        this.editingPrice = item.price;
      }
      this.showPriceModal = true;
    },
    viewPriceAndStock(item) {
      this.selectedPriceItem = item;
      // Cargar el producto
      const originalProduct = item.productObj || item;
      this.showInfoStockModal = true;
    },
    savePriceChanges() {
      if (this.selectedPriceItem) {
        this.selectedPriceItem.selectedPriceType = this.selectedPriceType;
        // Guardar ambos precios en el item
        this.selectedPriceItem.wholesalePrice = parseFloat(this.editingWholesalePrice) || 0;
        // Solo actualizar el precio activo según la selección
        if (this.selectedPriceType === 'normal') {
          this.selectedPriceItem.price = parseFloat(this.editingPrice);
        } else {
          this.selectedPriceItem.price = parseFloat(this.editingWholesalePrice);
        }
        // Actualizar el subtotal
        this.selectedPriceItem.subtotal = this.selectedPriceItem.price * this.selectedPriceItem.quantity;
      }
      this.closePriceModal();
    },
    closePriceModal() {
      this.showPriceModal = false;
      this.selectedPriceItem = null;
      this.editingPrice = 0;
      this.editingWholesalePrice = 0;
      this.selectedPriceType = 'normal';
    },
    closeInfoStockModal() {
      this.showInfoStockModal = false;
      this.selectedPriceItem = null;
    },
    searchProducts() {
      // Implementar lógica de búsqueda adicional si es necesario
    },
    getStockClass(stock) {
      if (stock <= 5) return 'stock-low';
      if (stock <= 10) return 'stock-medium';
      return 'stock-high';
    },
    getStockBadgeClass(stock) {
      if (stock <= 5) return 'badge-danger';
      if (stock <= 10) return 'badge-warning';
      return 'badge-success';
    }
  }
}
</script>

<style scoped>
.pos-container {
  height: 80vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

.pos-header {
  background: white;
  padding: 0.5rem;
  border-bottom: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.view-controls .form-check-label {
  font-size: 0.9rem;
  cursor: pointer;
}

.total-amount {
  font-size: 1.2rem;
  font-weight: bold;
  color: #28a745;
}

.pos-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.best-sellers-panel {
  width: 20%;
  background: white;
  border-right: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.best-sellers-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.best-seller-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
}

.best-seller-item:hover {
  background: #e9ecef;
  transform: translateY(-1px);
}

.product-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 0.25rem;
  margin-right: 0.75rem;
}

.product-info {
  flex: 1;
}

.product-name {
  font-size: 0.65rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.product-price {
  font-size: 0.8rem;
  color: #28a745;
  font-weight: 500;
}

.stock-badge {
  font-size: 0.6rem;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-weight: 500;
}

.stock-low { background: #dc3545; color: white; }
.stock-medium { background: #ffc107; color: #212529; }
.stock-high { background: #28a745; color: white; }

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1rem;
}

.main-content.full-width {
  width: 100%;
}

.search-section {
  margin-bottom: 0.4rem;
}

.search-input-group {
  display: flex;
  max-width: 500px;
}

.search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.search-btn {
  background: #0f172a;
  color: white;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.products-section {
  flex: 1;
  overflow-y: auto;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.product-card {
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: all 0.3s;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.product-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.stock-overlay {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.product-details {
  padding: 1rem;
}

/* .product-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #212529;
} */

.product-code {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.75rem;
}

.price-info {
  display: flex;
  justify-content: space-between;
}

.purchase-price {
  font-size: 0.85rem;
  color: #6c757d;
}

.sale-price {
  font-size: 0.9rem;
  font-weight: 600;
  color: #28a745;
}

.products-list {
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
}

.list-header {
  background: #f8f9fa;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
}

.list-item {
  padding: 1rem;
  border-bottom: 1px solid #f8f9fa;
  cursor: pointer;
  transition: background-color 0.2s;
}

.list-item:hover {
  background: #f8f9fa;
}

.list-image {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 0.25rem;
}

.badge-danger { background: #dc3545; }
.badge-warning { background: #ffc107; color: #212529; }
.badge-success { background: #28a745; }

.cart-panel {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 400px;
  max-height: 50vh;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem 0.5rem 0 0;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
}

.cart-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.cart-items {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.cart-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
}

.item-info {
  flex: 1;
  margin-right: 1rem;
}

.item-name {
  font-weight: 600;
  font-size: 0.6rem;
}

.item-price {
  font-size: 0.6rem;
  color: #6c757d;
}

.item-controls {
  display: flex;
  align-items: center;
  margin-right: 0.6rem;
}

.item-controls button {
  padding: 0.1rem 0.3rem !important;
}

.quantity-display {
  margin: 0 0.5rem;
  padding: 0.15rem 0.2rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  cursor: pointer;
  min-width: 30px;
  text-align: center;
}

.item-total {
  font-weight: 600;
  color: #28a745;
  margin-right: 0.5rem;
  min-width: 80px;
  text-align: right;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.quantity-modal {
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  min-width: 300px;
}

.cart-panel-side {
  width: 22%;
  background: white;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  min-width: 320px;
  max-width: 400px;
}

.cart-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.cart-items {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.cart-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-toggle {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 1.5rem;
  padding: 0.2rem 1.2rem;
  font-weight: 500;
  color: #0f172a;
  transition: background 0.2s, box-shadow 0.2s, color 0.2s;
  box-shadow: 0 1px 2px rgba(15,23,42,0.03);
  cursor: pointer;
}

.btn-toggle.active {
  background: #42b883;
  color: #0f172a;
  border-color: #0f172a;
  box-shadow: 0 2px 8px rgba(15,23,42,0.08);
}

.btn-group-toggle {
  display: flex;
  gap: 0.5rem;
}

.btn-toggle i {
  margin-right: 0.5rem;
}

.cart-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.total-pill {
  background: #f4f5f7;
  color: #212529;
  font-size: 1.15rem;
  font-weight: 600;
  border-radius: 1.5rem;
  padding: 0.5rem 1.2rem;
  display: flex;
  align-items: center;
}

.btn-quote {
  background: #2684ff;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  font-weight: 500;
  padding: 0.5rem 1.2rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  transition: background 0.2s;
}

.btn-quote:hover {
  background: #1565c0;
}

.btn-finish {
  background: #22c55e;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  font-weight: 500;
  padding: 0.5rem 1.2rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  transition: background 0.2s;
}

.btn-finish:hover {
  background: #16a34a;
}

.btn-quote i,
.btn-finish i {
  margin-right: 0.5rem;
}




.clickeable-price {
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  transition: background-color 0.2s;
}

.clickeable-price:hover {
  background-color: #e9ecef;
}

.price-type-indicator {
  color: #007bff;
  font-weight: 500;
}

.price-modal {
  background: white;
  border-radius: 0.5rem;
  min-width: 400px;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
}

.price-modal .modal-header {
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  border-radius: 0.5rem 0.5rem 0 0;
}

.price-modal .modal-header h6 {
  margin: 0;
  font-weight: 600;
  color: #495057;
}

.price-modal .modal-body {
  padding: 1.5rem;
}

.price-option {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 1rem;
  background: #f8f9fa;
  margin-bottom: 1rem;
}

.radio-container {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.form-check-input {
  width: 18px;
  height: 18px;
  margin-right: 0.5rem;
  margin-left: 0.7rem !important;
  border: 2px solid #dee2e6;
  border-radius: 50%;
  background-color: white;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 0.6;
}

.price-modal .modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  border-radius: 0 0 0.5rem 0.5rem;
}

.btn-secondary {
  background: #6c757d;
  border-color: #6c757d;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
}

.btn-primary {
  background: #007bff;
  border-color: #007bff;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
}

.price-modal .form-check-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0;
  font-weight: 500;
  color: #495057;
}

.price-modal .form-check-label i {
  margin-right: 0.5rem;
  font-size: 0.8rem;
}





@media (max-width: 768px) {
  .best-sellers-panel {
    width: 25%;
  }
  
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
  
  .cart-panel {
    width: 100%;
    max-height: 40vh;
  }
}

@media (max-width: 576px) {
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
}

@media (max-width: 992px) {
  .pos-container {
    height: 100vh;
  }

  .pos-header {
    padding: 0.5rem;
  }

  .header-controls {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .btn-toggle {
    font-size: 0.85rem;
    padding: 0.35rem 0.8rem;
  }

  .cart-summary {
    flex-wrap: wrap;
    gap: 0.3rem;
  }

  .total-pill {
    font-size: 0.95rem;
    padding: 0.4rem 0.8rem;
  }

  .btn-quote,
  .btn-finish {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
  }

  /* Ocultar panel de más vendidos en mobile */
  .best-sellers-panel {
    display: none;
  }

  /* Panel principal ocupa todo el ancho */
  .main-content {
    width: 100% !important;
    padding: 0.5rem;
  }

  /* Ocultar carrito lateral en mobile */
  .cart-panel-side {
    display: none;
  }

  .search-input-group {
    max-width: 100%;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  .product-card {
    font-size: 0.85rem;
  }

  .product-image {
    height: 120px;
  }

  .product-details {
    padding: 0.5rem;
  }

  /* Carrito flotante en mobile (abajo) */
  .cart-panel-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    border-top: 2px solid #dee2e6;
    box-shadow: 0 -4px 12px rgba(0,0,0,0.15);
    z-index: 999;
    max-height: 50vh;
    display: flex;
    flex-direction: column;
  }

  .cart-mobile-summary {
    padding: 0.75rem;
    background: #f8f9fa;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #dee2e6;
    cursor: pointer;
  }

  .cart-mobile-items {
    overflow-y: auto;
    max-height: 40vh;
    padding: 0.5rem;
  }
}

@media (max-width: 576px) {
  #buttonMasVendidos {
    display: none;
  }

  .pos-header {
    padding: 0.3rem;
  }

  .header-controls {
    width: 100%;
  }

  .cart-summary {
    width: 100%;
    justify-content: space-between;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.4rem;
  }

  .product-card {
    font-size: 0.75rem;
  }

  .product-image {
    height: 100px;
  }

  .btn-toggle {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
  }

  .total-pill {
    font-size: 0.85rem;
  }

  .btn-quote,
  .btn-finish {
    font-size: 0.75rem;
    padding: 0.35rem 0.6rem;
  }
}
</style>
